import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { httpOk, GET, POST, PUT, FILE, resolveUploadUrl } from 'helpers/api'
import WebsitePreview from './WebsitePreview'
import ThemePicker, { defaultTheme } from 'pages/parts/ThemePicker'
import ChannelPosesMenu, { saveChannelPosesMenu } from 'pages/parts/ChannelPosesMenu'
import PageTabs from 'pages/parts/PageTabs'
import { Link } from 'react-router'

const defaultData = {
	name: '',
	brand_name: '',
	slug: '',
	pos_id: null,
	domain: '',
	description: '',
	logo: '',
	favicon: '',
	theme_id: null,
	colors: null,
	themes: [],
	is_active: true,
	languages: [],
	poses: [],
	external_menu_id: null,
	order_types: [],
	payment_types: [],
	payment_type_id: null,
	settings: {
		show_categories: true,
		show_hit_products: true,
		show_contacts: true,
		rounded_cards: true,
	},
}

function mergeOrderTypes(savedOrderTypes = [], allOrderTypes = []) {
	return allOrderTypes.map((type) => {
		const orderTypeId = Number(type.id)
		const saved = (savedOrderTypes ?? []).find((item) => (
			Number(item.order_type_id ?? item.id) === orderTypeId
		))

		return {
			...type,
			order_type_id: orderTypeId,
			order_type_sub_id: saved?.order_type_sub_id ?? null,
		}
	})
}

function WebsiteBuilder() {
	const { t } = useTranslation()

	const [data, setData] = useState(defaultData)
	const [activeTab, setActiveTab] = useState('general')
	const [poses, setPoses] = useState([])
	const [languages, setLanguages] = useState([])
	const [deliveryRules, setDeliveryRules] = useState([])
	const [orderTypeSubs, setOrderTypeSubs] = useState([])
	const [paymentTypes, setPaymentTypes] = useState([])
	const [externalMenus, setExternalMenus] = useState([])
	const [priceCategories, setPriceCategories] = useState([])
	const [posMenus, setPosMenus] = useState({})

	async function load() {
		const response = await GET('/chain/website', {}, { loader: true })
		if (httpOk(response)) {
			const payload = {
				...defaultData,
				...response.data,
				settings: {
					...defaultData.settings,
					...(response.data.settings ?? {}),
				},
			}

			if (!payload.theme_id && payload.themes?.length) {
				const theme = defaultTheme(payload.themes)
				payload.theme_id = theme?.id ?? null
				payload.colors = theme?.colors ?? null
			}
			const responseOrderTypes = await GET('/helper/order-types')
			if (httpOk(responseOrderTypes)) {
				payload.order_types = mergeOrderTypes(payload.order_types, responseOrderTypes.data)
			}
			setData(payload)
		}
	}

	async function loadHelpers() {
		const [posesResponse, languagesResponse, deliveryRulesResponse, orderTypesResponse, paymentTypesResponse, externalMenusResponse, priceCategoriesResponse] = await Promise.all([
			GET('/helper/poses'),
			GET('/helper/languages'),
			GET('/helper/delivery-rules'),
			GET('/helper/order-type-subs'),
			GET('/helper/payment-types'),
			GET('/helper/external-menu'),
			GET('/price-categories'),
		])

		if (httpOk(posesResponse)) setPoses(posesResponse.data)
		if (httpOk(languagesResponse)) setLanguages(languagesResponse.data)
		if (httpOk(deliveryRulesResponse)) setDeliveryRules(deliveryRulesResponse.data)
		if (httpOk(orderTypesResponse)) setOrderTypeSubs(orderTypesResponse.data)
		if (httpOk(paymentTypesResponse)) setPaymentTypes(paymentTypesResponse.data)
		if (httpOk(externalMenusResponse)) setExternalMenus(externalMenusResponse.data)
		if (httpOk(priceCategoriesResponse)) setPriceCategories(priceCategoriesResponse.data?.data ?? [])
	}

	async function save(e) {
		e.preventDefault()
		const payload = {
			...data,
			order_types: (data.order_types ?? []).map((item) => ({
				order_type_id: item.order_type_id ?? item.id,
				order_type_sub_id: item.order_type_sub_id,
			})),
		}
		const response = data.id
			? await PUT('/chain/website', payload, { loader: true, notification: true })
			: await POST('/chain/website', payload, { loader: true, notification: true })

		if (httpOk(response)) {
			await saveChannelPosesMenu(posMenus)
			load()
		}
	}

	function change(key, value) {
		setData(prev => ({ ...prev, [key]: value }))
	}

	function changeTheme(option) {
		setData(prev => ({
			...prev,
			theme_id: option?.id ?? null,
			colors: option?.colors ?? null,
		}))
	}

	function changeDefaultPos(option) {
		const posId = option?.id ?? null
		const currentPoses = data.poses?.length ? data.poses : poses
		const websitePoses = currentPoses.map(item => (
			Number(item.id) === Number(posId) ? { ...item, status: true } : item
		))

		setData(prev => ({
			...prev,
			pos_id: posId,
			poses: websitePoses,
		}))
	}

	function changePosStatus(posId, checked) {
		const currentPoses = data.poses?.length ? data.poses : poses
		const websitePoses = currentPoses.map(item => (
			item.id === posId ? { ...item, status: checked } : item
		))
		setData({ ...data, poses: websitePoses })
	}

	function handleSelectOrderTypeSub(option, index) {
		const orderTypes = [...data.order_types]
		orderTypes[index] = { ...orderTypes[index], order_type_sub_id: option.id }
		setData({ ...data, order_types: orderTypes })
	}

	async function uploadImageFile(file, target, index = null) {
		if (!file) return
		const formData = new FormData()
		formData.append('image', file)
		formData.append('path', 'websites')

		const response = await FILE('/upload-image-only', formData, { loader: true })
		if (!httpOk(response)) return

		if (target === 'logo') {
			setData(prev => ({ ...prev, logo: response.data.url, favicon: response.data.url }))
			return
		}

		change(target, response.data.url)
	}

	async function uploadImage(e, target, index = null) {
		await uploadImageFile(e.target.files?.[0], target, index)
		e.target.value = ''
	}

	async function dropLogoImage(e) {
		e.preventDefault()
		await uploadImageFile(e.dataTransfer.files?.[0], 'logo')
	}

	function removeLogo() {
		setData(prev => ({ ...prev, logo: '', favicon: '' }))
	}

	useEffect(() => {
		load()
		loadHelpers()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const activePoses = data.poses?.length ? data.poses : poses
	const menuOptions = [
		{ id: null, name: 'Основное меню' },
		...externalMenus,
	]
	const selectedMenuOption = data.external_menu_id
		? menuOptions.find(option => Number(option.id) === Number(data.external_menu_id))
		: menuOptions[0]
	const selectedPosOption = data.pos_id
		? activePoses.find(option => Number(option.id) === Number(data.pos_id))
		: null

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">Конструктор сайта</h4>
				<button className="btn btn-primary" onClick={save}>Сохранить</button>
			</div>

			<PageTabs
				active={activeTab}
				onChange={setActiveTab}
				tabs={[
					{ key: 'general', label: t('general') },
					{ key: 'theme', label: t('theme') },
					{ key: 'orders', label: t('orders') },
					{ key: 'banners', label: t('banners') },
					{ key: 'poses', label: t('poses') },
					{ key: 'preview', label: t('preview') },
				]} />

			{activeTab === 'preview'
				? <WebsitePreview domain={data.domain} />
				: <form className="page-compact" onSubmit={save} autoComplete="off">
					<div className={activeTab !== 'general' ? 'd-none' : 'card'}>
						<div className="card-body">
							<div className="row">
								<div className="col-lg-6">
									<div className="row">
										<div className="col-sm-6 form-group">
											<label>Название<span className="required-mark">*</span></label>
											<input className="form-control" value={data.name ?? ''} onChange={e => change('name', e.target.value)} />
										</div>
										<div className="col-sm-6 form-group">
											<label>Название бренда</label>
											<input className="form-control" value={data.brand_name ?? ''} onChange={e => change('brand_name', e.target.value)} />
										</div>
									</div>
									<div className="form-group">
										<label>Домен</label>
										<input className="form-control" value={data.domain ?? ''} onChange={e => change('domain', e.target.value)} placeholder="example.mison.uz" />
									</div>
									<div className="form-group">
										<label>Описание</label>
										<textarea className="form-control" rows="3" value={data.description ?? ''} onChange={e => change('description', e.target.value)} />
									</div>
								</div>
								<div className="col-lg-6">
									<div className="form-group">
										<label>Торговая точка по умолчанию<span className="required-mark">*</span></label>
										<Select
											options={activePoses}
											value={selectedPosOption || null}
											onChange={changeDefaultPos}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
									<div className="form-group">
										<label>Меню сайта<span className="required-mark">*</span></label>
										<Select
											options={menuOptions}
											value={selectedMenuOption || null}
											onChange={option => {
												change('external_menu_id', option?.id ?? null)
											}}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id ?? 'main'}
											placeholder=""
										/>
										<Select
											isClearable
											className="mt-2"
											options={priceCategories}
											value={priceCategories.find(option => option.id === data.price_category_id) || ''}
											onChange={option => change('price_category_id', option ? option.id : null)}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id}
											placeholder="Базовая цена"
										/>
									</div>
									<div className="form-group">
										<label>Язык по умолчанию<span className="required-mark">*</span></label>
										<Select
											options={languages}
											value={languages.find(option => option.id === data.default_language_id) || null}
											onChange={option => change('default_language_id', option ? option.id : null)}
											getOptionLabel={option => `${option.native_name ?? option.name} (${option.code})`}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
									<div className="form-group mb-0">
										<label>Языки сайта<span className="required-mark">*</span></label>
										<Select
											isMulti
											options={languages}
											value={data.languages ?? []}
											onChange={options => change('languages', options ?? [])}
											getOptionLabel={option => `${option.native_name ?? option.name} (${option.code})`}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className={activeTab !== 'theme' ? 'd-none' : 'card'}>
						<div className="card-body">
							<div className="row mb-3">
								<div className="col-sm-6 col-lg-3">
									<div className="form-group mb-0">
										<label>Лого</label>
										<div className="border rounded overflow-hidden bg-light">
											<label
												className="d-flex align-items-center justify-content-center mb-0 cursor"
												onDragOver={e => e.preventDefault()}
												onDrop={dropLogoImage}
												style={{
													minHeight: 150,
													background: data.logo ? `url(${resolveUploadUrl(data.logo, 'websites')}) center / contain no-repeat` : '#f8f9fa',
												}}>
												{!data.logo &&
													<div className="text-center text-muted px-3">
														<i className="fa fa-image d-block mb-2" style={{ fontSize: 28 }} />
														<span>Загрузить лого</span>
														<div className="small mt-1">Favicon сформируется из этого фото</div>
													</div>
												}
												<input type="file" className="d-none" accept="image/*" onChange={e => uploadImage(e, 'logo')} />
											</label>
											<div className="d-flex justify-content-between align-items-center p-2 bg-white">
												<small className="text-muted">Лого и favicon</small>
												<button type="button" className="btn btn-sm btn-outline-danger" disabled={!data.logo} onClick={removeLogo}>
													Удалить
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
							<ThemePicker
								title="Тема сайта"
								required
								themes={data.themes ?? []}
								value={data.theme_id}
								onChange={changeTheme}
							/>
						</div>
					</div>

					<div className={activeTab !== 'orders' ? 'd-none' : 'card'}>
						<div className="card-body">
							<div className="row">
								<div className="col-md-6 col-xl-4">
									<small className="text-muted d-block mb-2">Доставка</small>
									<div className="form-group">
										<label>Правило доставки<span className="required-mark">*</span></label>
										<Select
											options={deliveryRules}
											value={deliveryRules.find(option => option.id === data.delivery_rule_id) || null}
											onChange={option => change('delivery_rule_id', option ? option.id : null)}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
									<div className="form-group mb-0">
										<label>Тип доставки<span className="required-mark">*</span></label>
										<div className="d-flex flex-column gap-2">
											{data?.order_types?.map((item, index) => (
												<div key={item.order_type_id ?? item.id ?? index} className="d-flex align-items-center gap-2">
													<span className="fw-600 flex-shrink-0" style={{ minWidth: 90 }}>{item.name}</span>
													<Select
														className="flex-grow-1"
														options={orderTypeSubs}
														value={orderTypeSubs.find(option => option.id === item.order_type_sub_id) || null}
														onChange={option => handleSelectOrderTypeSub(option, index)}
														getOptionLabel={option => option.name}
														getOptionValue={option => option.id}
														placeholder=""
													/>
												</div>
											))}
										</div>
									</div>
								</div>
								<div className="col-md-6 col-xl-4">
									<small className="text-muted d-block mb-2">Оплата</small>
									<div className="form-group">
										<label>Типы оплат<span className="required-mark">*</span></label>
										<Select
											isMulti
											options={paymentTypes}
											value={data.payment_types ?? []}
											onChange={options => change('payment_types', options ?? [])}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
									<div className="form-group mb-0">
										<label>По умолчанию<span className="required-mark">*</span></label>
										<Select
											options={paymentTypes}
											value={paymentTypes.find(option => option.payment_type_id === data.payment_type_id) || null}
											onChange={option => change('payment_type_id', option ? option.payment_type_id : null)}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.payment_type_id}
											placeholder=""
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Баннеры общие на бренд: грузятся один раз в «Бренд → Баннеры», канал отмечается галочкой */}
					<div className={activeTab !== 'banners' ? 'd-none' : 'card'}>
						<div className="card-body">
							<p className="mb-3">{t('brand_banners_moved')}</p>
							<Link to="/brand" className="btn btn-primary">{t('brand_banners_open')}</Link>
						</div>
					</div>

					<div className={activeTab !== 'poses' ? 'd-none' : 'card'}>
						<div className="card-body">
							<ChannelPosesMenu
								poses={activePoses}
								overrides={posMenus}
								setOverrides={setPosMenus}
								onToggle={(e, index) => changePosStatus(activePoses[index].id, e.target.checked)} />
						</div>
					</div>
				</form>
			}
		</>
	)
}

export default WebsiteBuilder
